import { useEffect, useMemo, useRef } from 'react'
import type {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table'
import {
  isPersistedStateDefault,
  makeTableStateKey,
  migrateLegacyVisibility,
  useTableStateStore,
  type PersistedTableState,
} from '@/shared/stores/table-state-store'
import { useAuth } from '@/shared/lib/auth/auth-context'

export interface UsePersistedTableStateOptions {
  /** Stable page identifier (e.g. "transactions", "bank-accounts"). */
  pageKey: string
  /** Legacy `dt-cols-<storageKey>` key to migrate from, if any. */
  legacyStorageKey?: string
}

export interface UsePersistedTableStateResult {
  /** Whether the auth layer has resolved the user identity. */
  isReady: boolean
  /** Current persisted state (undefined when nothing has been saved). */
  state: PersistedTableState | undefined
  /** True when the persisted state has any non-default content. */
  hasSavedView: boolean
  saveColumnFilters: (filters: ColumnFiltersState) => void
  saveColumnVisibility: (visibility: VisibilityState) => void
  saveSorting: (sorting: SortingState) => void
  saveExtras: (extras: Record<string, unknown>) => void
  reset: () => void
}

/**
 * Binds a page to the shared table state store with automatic namespacing by
 * authenticated user. Handles one-time migration from the legacy
 * `dt-cols-<storageKey>` localStorage blob.
 */
export function usePersistedTableState({
  pageKey,
  legacyStorageKey,
}: UsePersistedTableStateOptions): UsePersistedTableStateResult {
  const { user, isInitialized } = useAuth()
  const userId = user?.userId
  const compositeKey = useMemo(() => makeTableStateKey(userId, pageKey), [userId, pageKey])

  const migratedKeysRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!isInitialized || !legacyStorageKey) return
    if (migratedKeysRef.current.has(compositeKey)) return
    migratedKeysRef.current.add(compositeKey)
    migrateLegacyVisibility(compositeKey, legacyStorageKey)
  }, [compositeKey, isInitialized, legacyStorageKey])

  const state = useTableStateStore((s) => s.states[compositeKey])

  const saveColumnFilters = (filters: ColumnFiltersState) =>
    useTableStateStore.getState().patchState(compositeKey, { columnFilters: filters })

  const saveColumnVisibility = (visibility: VisibilityState) =>
    useTableStateStore.getState().patchState(compositeKey, { columnVisibility: visibility })

  const saveSorting = (sorting: SortingState) =>
    useTableStateStore.getState().patchState(compositeKey, { sorting })

  const saveExtras = (extras: Record<string, unknown>) =>
    useTableStateStore.getState().patchState(compositeKey, { extras })

  const reset = () => useTableStateStore.getState().clearState(compositeKey)

  return {
    isReady: isInitialized,
    state,
    hasSavedView: !isPersistedStateDefault(state),
    saveColumnFilters,
    saveColumnVisibility,
    saveSorting,
    saveExtras,
    reset,
  }
}
